import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  createProject,
  deleteProject,
  getProjectById,
  getProjectBySlug,
  queryProjects,
  rotateProjectApiKey,
  testProjectPipelineWebhook,
  updateProject,
  wipeProjectData,
} from '@/features/projects/data';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { ApiError } from '@/shared/data/client';
import {
  gridPaths,
  invalidateCurrentUserRoles,
  invalidateGridQuery,
  invalidateProjectById,
  invalidateProjectDetail,
  invalidateProjects,
  queryKeys,
  useInvalidateQueries,
  useIsAuthenticated,
} from '@/shared/hooks';
import { removeFromResult, upsertIntoResult } from '@/shared/hooks/shared';
import {
  mergeProjectThresholdGroup,
  type ProjectMetricThresholdUpdate,
} from '../utils/projectMetricThresholds';
import { isWipeDataQueryKey } from './wipeQueryKeys';
import type {
  ProjectApiKeyRotateResult,
  ProjectPipelineWebhookTestResult,
  ProjectWipeDataInput,
  ProjectWipeDataResult,
} from '@/features/projects/data/projects';
import type { ProjectDto, QueryResult } from '@flowqa/shared';

type CreateProjectInput = {
  name: string;
  badgeChars?: ProjectDto['badgeChars'];
  badgeColor?: ProjectDto['badgeColor'];
  orgId?: string | null;
};
type UpdateProjectInput = {
  id: string;
  name?: string;
  badgeEnabled?: boolean;
  badgeChars?: ProjectDto['badgeChars'];
  badgeColor?: ProjectDto['badgeColor'];
  projectApiKey?: null;
  metricThresholds?: ProjectMetricThresholdUpdate;
};
type DeleteProjectInput = { id: string };
type TestProjectPipelineWebhookInput = { id: string; apiKey: string };
type RotateProjectApiKeyInput = { id: string; tags?: string[] };

const isProjectContextLostError = (error: ApiError): boolean => {
  if (error.status === 404 && error.code === 'NOT_FOUND') {
    return error.message.toLowerCase().includes('project not found');
  }
  return error.status === 401 || error.status === 403;
};

export const useProjectsQuery = (options?: {
  staleTimeMs?: number;
  refetchOnWindowFocus?: boolean;
  enabled?: boolean;
}): UseQueryResult<QueryResult<ProjectDto>, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  return useQuery<QueryResult<ProjectDto>, ApiError>({
    queryKey: queryKeys.projects(orgKey),
    enabled: isAuthenticated && (options?.enabled ?? true),
    queryFn: async (): Promise<QueryResult<ProjectDto>> => {
      const result = await queryProjects(orgForQuery);
      if (selectedOrganisationId === null) {
        const items = result.items.filter((proj) => !proj.organisationId);
        return { ...result, items, total: items.length };
      }
      return result;
    },
    staleTime: options?.staleTimeMs ?? 5 * 60_000,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
  });
};

export const useProjectDetailQuery = (
  slug: string | null,
): UseQueryResult<ProjectDto | null, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  return useQuery<ProjectDto | null, ApiError>({
    queryKey: queryKeys.projectDetail(slug, orgKey),
    enabled: isAuthenticated && Boolean(slug),
    queryFn: async (): Promise<ProjectDto | null> => {
      try {
        return await getProjectBySlug(slug, orgForQuery);
      } catch (error) {
        if (error instanceof ApiError && isProjectContextLostError(error)) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 15_000,
  });
};

export const useProjectByIdQuery = (
  projectId: string | null,
  options?: { staleTimeMs?: number; refetchOnWindowFocus?: boolean },
): UseQueryResult<ProjectDto | null, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  const query = useQuery<ProjectDto | null, ApiError>({
    queryKey: queryKeys.projectById(projectId, orgKey),
    enabled: isAuthenticated && Boolean(projectId),
    queryFn: async (): Promise<ProjectDto | null> => {
      if (!projectId) return null;
      try {
        return await getProjectById(projectId, orgForQuery);
      } catch (error) {
        if (error instanceof ApiError && isProjectContextLostError(error)) {
          return null;
        }
        throw error;
      }
    },
    staleTime: options?.staleTimeMs ?? 15_000,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
  });

  useEffect(() => {
    if (!projectId || selectedProjectId !== projectId) {
      return;
    }
    if (!query.isFetched || query.data !== null) {
      return;
    }
    setSelectedProjectId(null);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('flowqa-project-removed', {
          detail: { id: projectId, reason: 'context_lost' },
        }),
      );
    }
  }, [projectId, query.data, query.isFetched, selectedProjectId, setSelectedProjectId]);

  return query;
};

export const useCreateProject = (): UseMutationResult<ProjectDto, ApiError, CreateProjectInput> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const projectsKey = queryKeys.projects();

  return useMutation<ProjectDto, ApiError, CreateProjectInput>({
    mutationFn: async (input: CreateProjectInput): Promise<ProjectDto> => {
      const { orgId, ...payload } = input;
      return await createProject(payload, orgId ?? undefined);
    },
    onSuccess: async (created) => {
      queryClient.setQueryData<QueryResult<ProjectDto>>(projectsKey, (prev) =>
        upsertIntoResult(prev, created),
      );
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent<ProjectDto>('flowqa-project-created', { detail: created }),
        );
      }
      await invalidateQueries([
        invalidateProjects(),
        invalidateGridQuery(gridPaths.projects),
        invalidateCurrentUserRoles(),
      ]);
    },
  });
};

export const useUpdateProject = (): UseMutationResult<
  ProjectDto,
  ApiError,
  UpdateProjectInput,
  { previousProjects?: QueryResult<ProjectDto> }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const projectsKey = queryKeys.projects();
  const { selectedOrganisationId } = useOrganisationFilter();

  return useMutation<
    ProjectDto,
    ApiError,
    UpdateProjectInput,
    { previousProjects?: QueryResult<ProjectDto> }
  >({
    mutationFn: async (input: UpdateProjectInput): Promise<ProjectDto> => {
      return await updateProject(input, selectedOrganisationId);
    },
    onMutate: (input) => {
      const previousProjects = queryClient.getQueryData<QueryResult<ProjectDto>>(projectsKey);
      if (previousProjects) {
        queryClient.setQueryData<QueryResult<ProjectDto>>(projectsKey, (prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((item) =>
                  item.id === input.id
                    ? ((() => {
                        if (!input.metricThresholds) {
                          return { ...item, ...input };
                        }
                        const nextMetricThresholds: ProjectDto['metricThresholds'] = {
                          suites: mergeProjectThresholdGroup(
                            item.metricThresholds.suites,
                            input.metricThresholds.suites,
                          ),
                          executionRuns: mergeProjectThresholdGroup(
                            item.metricThresholds.executionRuns,
                            input.metricThresholds.executionRuns,
                          ),
                          tests: mergeProjectThresholdGroup(
                            item.metricThresholds.tests,
                            input.metricThresholds.tests,
                          ),
                          pipelines: mergeProjectThresholdGroup(
                            item.metricThresholds.pipelines,
                            input.metricThresholds.pipelines,
                          ),
                          stages: mergeProjectThresholdGroup(
                            item.metricThresholds.stages,
                            input.metricThresholds.stages,
                          ),
                          jobs: mergeProjectThresholdGroup(
                            item.metricThresholds.jobs,
                            input.metricThresholds.jobs,
                          ),
                        };
                        return { ...item, ...input, metricThresholds: nextMetricThresholds };
                      })() as ProjectDto)
                    : item,
                ),
              }
            : prev,
        );
      }
      return { previousProjects };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(projectsKey, context.previousProjects);
      }
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData<QueryResult<ProjectDto>>(projectsKey, (prev) =>
        upsertIntoResult(prev, updated),
      );
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent<ProjectDto>('flowqa-project-updated', { detail: updated }),
        );
      }
      await invalidateQueries([
        invalidateProjects(),
        invalidateProjectById(updated.id, selectedOrganisationId ?? null),
        invalidateProjectDetail(updated.slug),
        invalidateGridQuery(gridPaths.projects),
      ]);
    },
  });
};

export const useDeleteProject = (): UseMutationResult<
  { id: string },
  ApiError,
  DeleteProjectInput,
  { previousProjects?: QueryResult<ProjectDto> }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const projectsKey = queryKeys.projects();
  const { selectedOrganisationId } = useOrganisationFilter();

  return useMutation<
    { id: string },
    ApiError,
    DeleteProjectInput,
    { previousProjects?: QueryResult<ProjectDto> }
  >({
    mutationFn: async (input: DeleteProjectInput): Promise<{ id: string }> => {
      return await deleteProject(input);
    },
    onMutate: (input) => {
      const previousProjects = queryClient.getQueryData<QueryResult<ProjectDto>>(projectsKey);
      if (previousProjects) {
        queryClient.setQueryData<QueryResult<ProjectDto>>(projectsKey, (prev) =>
          removeFromResult(prev, input.id),
        );
      }
      return { previousProjects };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(projectsKey, context.previousProjects);
      }
    },
    onSuccess: async (deleted) => {
      queryClient.setQueryData<QueryResult<ProjectDto>>(projectsKey, (prev) =>
        removeFromResult(prev, deleted.id),
      );
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent<{ id: string; organisationId: string | null }>('flowqa-project-removed', {
            detail: { id: deleted.id, organisationId: selectedOrganisationId ?? null },
          }),
        );
      }
      await invalidateQueries([
        invalidateProjects(),
        invalidateProjectById(deleted.id, selectedOrganisationId ?? null),
        invalidateProjectDetail(null, selectedOrganisationId ?? null),
        invalidateGridQuery(gridPaths.projects),
      ]);
    },
  });
};

export const useTestProjectPipelineWebhook = (): UseMutationResult<
  ProjectPipelineWebhookTestResult,
  ApiError,
  TestProjectPipelineWebhookInput
> => {
  const queryClient = useQueryClient();
  const { selectedOrganisationId } = useOrganisationFilter();
  return useMutation<ProjectPipelineWebhookTestResult, ApiError, TestProjectPipelineWebhookInput>({
    mutationFn: async (
      input: TestProjectPipelineWebhookInput,
    ): Promise<ProjectPipelineWebhookTestResult> =>
      await testProjectPipelineWebhook(input, selectedOrganisationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projectDetail(null, selectedOrganisationId ?? null),
        exact: false,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projectById(null, selectedOrganisationId ?? null),
        exact: false,
      });
    },
  });
};

export const useRotateProjectApiKey = (): UseMutationResult<
  ProjectApiKeyRotateResult,
  ApiError,
  RotateProjectApiKeyInput
> => {
  const queryClient = useQueryClient();
  const { selectedOrganisationId } = useOrganisationFilter();
  return useMutation<ProjectApiKeyRotateResult, ApiError, RotateProjectApiKeyInput>({
    mutationFn: async (input: RotateProjectApiKeyInput): Promise<ProjectApiKeyRotateResult> =>
      await rotateProjectApiKey(input, selectedOrganisationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projectDetail(null, selectedOrganisationId ?? null),
        exact: false,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projectById(null, selectedOrganisationId ?? null),
        exact: false,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects(), exact: false });
    },
  });
};

export const useWipeProjectData = (): UseMutationResult<
  ProjectWipeDataResult,
  ApiError,
  ProjectWipeDataInput
> => {
  const queryClient = useQueryClient();
  return useMutation<ProjectWipeDataResult, ApiError, ProjectWipeDataInput>({
    mutationFn: async (input: ProjectWipeDataInput): Promise<ProjectWipeDataResult> => {
      return await wipeProjectData(input);
    },
    onSuccess: async (result) => {
      if (result.status === 'wiped') {
        await queryClient.cancelQueries({
          predicate: (query) => isWipeDataQueryKey(query.queryKey),
        });
        queryClient.removeQueries({
          predicate: (query) => isWipeDataQueryKey(query.queryKey),
        });
        await queryClient.refetchQueries({
          predicate: (query) => isWipeDataQueryKey(query.queryKey),
          type: 'active',
        });
        return;
      }

      await queryClient.invalidateQueries({
        predicate: (query) => isWipeDataQueryKey(query.queryKey),
      });
    },
  });
};

export const useInvalidateProjects = (): (() => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  return useCallback(async (): Promise<void> => {
    await invalidateQueries([invalidateProjects(), invalidateGridQuery(gridPaths.projects)]);
  }, [invalidateQueries]);
};

export const useInvalidateProjectDetail = (): ((slug: string | null) => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  return useCallback(
    async (slug: string | null): Promise<void> => {
      await invalidateQueries([invalidateProjectDetail(slug)]);
    },
    [invalidateQueries],
  );
};
